import * as React from 'react';
import { createPortal } from 'react-dom';

import { HighlightingBackground, HighlightingTooltip } from '../components/Highlighting';

type HighlightingContextValue = {
  highlightRef: React.MutableRefObject<HTMLElement | null>;
  enableHighlighting: (tooltipContent: React.ReactNode) => void;
  disableHighlighting: () => void;
};

const HighlightingContext = React.createContext<HighlightingContextValue | undefined>(undefined);

export const HighlightingProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const highlightRef = React.useRef<HTMLElement | null>(null);
  const [isActive, setIsActive] = React.useState(false);
  const [tooltipTop, setTooltipTop] = React.useState(0);
  const [tooltipContent, setTooltipContent] = React.useState<React.ReactNode>(null);

  const enableHighlighting = React.useCallback((tooltipContent: React.ReactNode) => {
    if (highlightRef.current) {
      highlightRef.current.style.zIndex = '4';
      highlightRef.current.style.pointerEvents = 'none';
      highlightRef.current.style.backgroundColor = 'white';
      highlightRef.current.style.position = 'relative';

      const rect = highlightRef.current.getBoundingClientRect();

      setTooltipTop(rect.bottom);
      setTooltipContent(tooltipContent);
      setIsActive(true);
    }
  }, []);

  const disableHighlighting = React.useCallback(() => {
    if (highlightRef.current) {
      highlightRef.current.style.zIndex = '';
      highlightRef.current.style.pointerEvents = 'auto';
      highlightRef.current.style.backgroundColor = '';
      highlightRef.current.style.position = '';
    }
    setIsActive(false);
  }, []);

  const handleBackgroundClick = (event: React.MouseEvent) => {
    if (highlightRef.current) {
      const rect = highlightRef.current.getBoundingClientRect();
      const isClickInsideHighlight =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      if (isClickInsideHighlight) {
        event.stopPropagation(); // Prevent the event from bubbling to the background

        return;
      }
    }
    disableHighlighting();
  };

  const contextValue = React.useMemo(
    () => ({ highlightRef, enableHighlighting, disableHighlighting }),
    [disableHighlighting, enableHighlighting],
  );

  return (
    <HighlightingContext.Provider value={contextValue}>
      {children}
      {isActive &&
        createPortal(<HighlightingTooltip top={tooltipTop}>{tooltipContent}</HighlightingTooltip>, document.body)}
      {isActive && createPortal(<HighlightingBackground onClick={handleBackgroundClick} />, document.body)}
    </HighlightingContext.Provider>
  );
};

export const useHighlighting = (): HighlightingContextValue => {
  const context = React.useContext(HighlightingContext);
  if (!context) {
    throw new Error('useHighlighting must be used within a HighlightingProvider');
  }

  return context;
};
